import type { Block } from "../content/types";
import { topic as anxietyAtNight } from "./anxiety-at-night";
import { topic as comparison } from "./comparison";
import { topic as encouragement } from "./encouragement";
import { topic as fasting } from "./fasting";
import { topic as forgiveness } from "./forgiveness";
import { topic as friendship } from "./friendship";
import { topic as godsLove } from "./gods-love";
import { topic as grief } from "./grief";
import { topic as hope } from "./hope";
import { topic as peace } from "./peace";
import { topic as prayer } from "./prayer";
import { topic as promises } from "./promises";
import { topic as protection } from "./protection";
import { topic as rest } from "./rest";
import { topic as selfControl } from "./self-control";
import { topic as strength } from "./strength";
import { topic as temptation } from "./temptation";
import { topic as time } from "./time";
import type { VerseTopic } from "./types";

/**
 * Every verse collection, in the order `/verses` lists them.
 *
 * Static, like the other two corpora — see `content/index.ts` for the reasoning
 * and for where the seam is when the whole thing outgrows one chunk.
 */
export const VERSE_TOPICS: VerseTopic[] = [
  selfControl,
  anxietyAtNight,
  strength,
  peace,
  rest,
  grief,
  forgiveness,
  comparison,
  time,
  temptation,
  promises,
  encouragement,
  friendship,
  prayer,
  godsLove,
  protection,
  hope,
  fasting,
];

export const TOPICS_BY_SLUG: Record<string, VerseTopic> = Object.fromEntries(
  VERSE_TOPICS.map((t) => [t.slug, t]),
);

export function verseTopicBySlug(slug: string): VerseTopic | undefined {
  return TOPICS_BY_SLUG[slug];
}

/** The path a collection lives at. One place, so the sitemap cannot drift. */
export function versePath(slug: string): string {
  return `/verses/${slug}`;
}

/**
 * Roughly how long the page takes to read.
 *
 * Counts the notes as well as the prose, since the notes are most of the page —
 * see `types.ts` for why that is the design rather than an accident.
 */
export function verseMinutes(topic: VerseTopic): number {
  const prose = [...topic.intro, ...topic.pray].reduce(
    (n, block) => n + blockWords(block),
    0,
  );
  const verses = topic.verses.reduce(
    (n, pick) => n + count(pick.verse.text) + count(pick.note),
    0,
  );
  return Math.max(1, Math.round((prose + verses) / 220));
}

function count(text: string): number {
  return text.trim() ? text.trim().split(/\s+/).length : 0;
}

function blockWords(block: Block): number {
  switch (block.type) {
    case "ul":
    case "ol":
      return count(block.items.join(" "));
    case "verse":
      return count(block.verse.text);
    default:
      return count(block.text);
  }
}

/** The `<h2>`s of a collection's prose, for its table of contents. */
export function verseOutline(
  topic: VerseTopic,
): { id: string; text: string }[] {
  return [...topic.intro, ...topic.pray].flatMap((block) =>
    block.type === "h2" ? [{ id: block.id, text: block.text }] : [],
  );
}

export type { VersePick, VerseTopic } from "./types";
